<template>
  <div :class="loading ? 'd-flex' : ''">
    <div class="pt-2" v-show="!loading">
      <template v-if="erro">
        <div class="na-dc-error">
          <font-awesome-icon icon="fa fa-triangle-exclamation" />
          <p class="mb-0 mt-2">{{ erro }}</p>
          <button type="button" class="na-dc-retry" @click="recarregar">Tentar novamente</button>
        </div>
      </template>

      <template v-else-if="detalhe">
        <!-- Identificação da estação (equivale ao seletor de sensor do device próprio) -->
        <div class="na-dc-source">
          <span class="na-label">Estação da rede pública</span>
          <div class="na-dc-source-box mt-1">
            <div class="na-dc-source-main">
              <span class="na-dc-source-name">Defesa Civil (RS)</span>
              <span class="na-dc-source-code">{{ detalhe.codigo }}</span>
            </div>
            <span class="na-dc-source-sub" v-if="localizacao">{{ localizacao }}</span>
          </div>
        </div>

        <!-- Frescor do dado antes de qualquer número: um valor de horas atrás lido como
             atual é pior que nenhum valor. -->
        <div class="na-section na-dc-leitura" :class="{ 'is-offline': detalhe.offline }">
          <div class="na-dc-leitura-row">
            <span class="na-dc-leitura-label">
              {{ detalhe.offline ? "Sem transmitir desde" : "Última leitura" }}
            </span>
            <span class="na-dc-leitura-date">{{ formatDate(detalhe.ultimaLeituraEm) }}</span>
            <span class="na-dc-leitura-time">{{ formatTime(detalhe.ultimaLeituraEm) }}</span>
          </div>
        </div>

        <div class="na-historico-header">
          <span class="na-historico-title">Histórico de leitura</span>
        </div>

        <!-- Acordeão de dias, mesmo padrão dos outros asides do mapa -->
        <div class="na-days-container" v-if="days.length">
          <div class="na-days-row">
            <button
              v-for="(d, i) in days"
              :key="d.data"
              :class="['na-day-item', { active: expandedDayIndex === i, 'is-empty': !temDados(d) }]"
              @click="onDaySelect(i)"
            >
              <div class="na-day-label">
                <span class="na-day-text">{{ dayLabel(d.data) }}</span>
              </div>

              <!-- Mesma anatomia do NewAsideDayItem (aside de dispositivo): linha do
                   dia, valor em destaque com unidade, e a faixa mín/máx embaixo.
                   Em chuva não existe mín/máx útil — o que interessa é quanto choveu.

                   Dias encerrados mostram só máx/mín: a média de um dia que já acabou não
                   descreve nenhum instante, enquanto os extremos resumem o que aconteceu.
                   Mesma regra dos outros asides do mapa. Chuva fica de fora — lá o número
                   é o total do dia, que continua valendo depois que o dia fecha. -->
              <div class="na-day-minmax" v-if="!mainEhSoma && i !== days.length - 1">
                <span class="na-day-minmax-item">
                  <span class="na-day-minmax-cap">máx</span>
                  <span class="na-day-minmax-val">{{ dayMainMax(d) }}</span>
                </span>
                <span class="na-day-minmax-item">
                  <span class="na-day-minmax-cap">mín</span>
                  <span class="na-day-minmax-val">{{ dayMainMin(d) }}</span>
                </span>
                <span class="na-day-minmax-unit">{{ mainUnit }}</span>
              </div>

              <template v-else>
                <div class="na-day-rain">
                  <span class="na-day-value">{{ dayMainValue(d) }}</span>
                  <span class="na-day-unit">{{ mainUnit }}</span>
                </div>

                <div class="na-day-temp" v-if="!mainEhSoma">
                  <span class="na-day-temp-range">{{ dayMainRange(d) }}</span>
                </div>
              </template>
            </button>
          </div>

          <div v-if="expandedDay" class="na-expanded-content" :style="expandedContentRadius">
            <div class="na-section na-leitura" v-if="ultimaHoraDoDia">
              <div class="na-leitura-row">
                <span class="na-leitura-label">{{ horarioExato ? "Última leitura" : "Último bloco com leitura" }}</span>
                <span class="na-leitura-date">{{ formatDate(ultimaHoraDoDia) }}</span>
                <span class="na-leitura-time">{{ formatTime(ultimaHoraDoDia) }}</span>
              </div>
            </div>

            <!-- Situação do rio só no dia corrente: nível e tendência descrevem AGORA, e
                 repetir isso num dia passado sugeriria que aquele é o estado daquele dia. -->
            <div class="na-dc-rio" v-if="isDiaAtual && nivelAtual != null">
              <div class="na-dc-rio-main">
                <span class="na-dc-rio-label">Nível do rio</span>
                <span class="na-dc-rio-value">{{ fmt(nivelAtual, 2) }} m</span>
              </div>
              <div class="na-dc-rio-trend" v-if="tendencia">
                <span class="na-dc-trend-glyph" :style="{ color: tendencia.cor }" v-html="tendencia.icone"></span>
                <span class="na-dc-rio-trend-label">{{ tendencia.label }}</span>
                <span class="na-dc-rio-rate" v-if="taxaTexto">{{ taxaTexto }}</span>
              </div>
            </div>

            <p class="na-dc-empty" v-if="!metricRows.length">
              Sem leitura nesta estação neste dia.
            </p>

            <!-- Cards das grandezas do dia; clicar abre o gráfico logo abaixo da linha -->
            <template v-for="(row, rowIdx) in metricRows" :key="rowIdx">
              <div class="na-metrics-grid">
                <NewAsideMetricCard
                  v-for="card in row"
                  :key="card.grandeza"
                  :label="card.label"
                  :value="card.value"
                  :unit="card.unit"
                  :icon-src="card.iconSrc"
                  :gradient="card.gradient"
                  :metric-key="card.grandeza"
                  :selected="selectedGrandeza === card.grandeza"
                  @select="selectCard(card.grandeza)"
                />
              </div>

              <div v-if="isRowSelected(row)" class="na-chart-table-card">
                <!-- show-min-max desligado de propósito: a origem entrega UM valor por hora
                     (a média do intervalo), então não há faixa mín/máx horária para desenhar
                     — inventar uma seria mentira. -->
                <NewAsideHistoryChart
                  :hours="chartHours"
                  metric-field="avg"
                  :show-min-max="false"
                  :chart-type="selectedEhSoma ? 'bar' : 'line'"
                  :metric-label="selectedLabel"
                  :metric-color="selectedCor"
                  :collapsed="historyCollapsed"
                  @toggle-collapse="historyCollapsed = !historyCollapsed"
                />
                <NewAsideDataTable :hours="chartHours" :columns="tableColumns" pending-label />
              </div>
            </template>
          </div>
        </div>

        <div v-else class="na-dc-empty">Sem leituras no período.</div>

        <!-- DESATIVADO TEMPORARIAMENTE (a pedido): bloco de chuva acumulada.
             Em avaliacao se o componente permanece no aside. O JS que o alimenta
             (temChuva, chuva24h, chuvaEstado, chuvaJanelas, chuvaMes,
             chuvaMesComparacao) e o CSS .na-rain-* seguem no arquivo, entao para
             reativar basta remover este comentario.
             Na marcacao abaixo os delimitadores de comentario foram trocados por
             <!~~ e ~~> porque HTML nao permite comentario aninhado.

        <!~~ Chuva acumulada. As janelas da origem são ANINHADAS (5min ⊂ 1h ⊂ … ⊂ 7d) e
             convivem com totais de mês, que são outra ordem de grandeza. Numa escala só,
             o mês anterior (centenas de mm) reduz as janelas curtas (poucos mm) a um
             traço invisível — por isso são dois blocos, cada um com a sua escala. ~~>
        <template v-if="temChuva">
          <div class="na-historico-header">
            <span class="na-historico-title">Chuva acumulada</span>
          </div>

          <div class="na-rain-card">
            <!~~ O número que a pessoa veio buscar primeiro. ~~>
            <div class="na-rain-hero">
              <span class="na-rain-hero-value">{{ chuva24h }}</span>
              <span class="na-rain-hero-unit">mm</span>
            </div>
            <div class="na-rain-hero-label">nas últimas 24 horas</div>
            <div class="na-rain-state" v-if="chuvaEstado">{{ chuvaEstado }}</div>

            <div class="na-rain-group" v-if="chuvaJanelas.length">
              <div class="na-rain-group-title">Janelas até agora</div>
              <div
                class="na-rain-row"
                :class="{ 'is-zero': j.zero }"
                v-for="j in chuvaJanelas"
                :key="j.chave"
              >
                <span class="na-rain-label">{{ j.label }}</span>
                <span class="na-rain-track">
                  <span class="na-rain-fill" :style="{ width: j.percentual + '%' }"></span>
                </span>
                <span class="na-rain-value">{{ j.texto }}</span>
              </div>
            </div>

            <div class="na-rain-group" v-if="chuvaMes.length">
              <div class="na-rain-group-title">Mês</div>
              <div class="na-rain-row" v-for="m in chuvaMes" :key="m.chave">
                <span class="na-rain-label">{{ m.label }}</span>
                <span class="na-rain-track">
                  <span class="na-rain-fill" :style="{ width: m.percentual + '%' }"></span>
                </span>
                <span class="na-rain-value">{{ m.texto }}</span>
              </div>
              <div class="na-rain-note" v-if="chuvaMesComparacao">{{ chuvaMesComparacao }}</div>
            </div>
          </div>
        </template>
        -->

        <p class="na-dc-fonte-nota">
          Dados da Rede Hidrometeorológica da Defesa Civil do RS. O OnAgro apenas exibe as
          leituras da fonte.
        </p>
      </template>
    </div>

    <div v-if="loading" class="text-center m-auto p-5">
      <Load color="#006035" />
    </div>
  </div>
</template>

<script>
import NewAsideMetricCard from "./NewAsideMetricCard.vue";
import NewAsideHistoryChart from "./NewAsideHistoryChart.vue";
import NewAsideDataTable from "./NewAsideDataTable.vue";
import Load from "../Load.vue";
import { DEFESA_CIVIL_GRANDEZAS } from "../../mixin/onagro.js";

const WEEK_ABBR = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

// Ícone de cada grandeza, nos mesmos SVGs que o aside de dispositivo usa nos cards.
// require() precisa de caminho LITERAL para o webpack resolver o asset, por isso o mapa
// é escrito por extenso em vez de montado por interpolação.
// Os arquivos não precisam ser as variantes "Black": NewAsideMetricCard aplica
// filter: brightness(0) no ícone, então qualquer SVG entra preto no card.
const ICONES = {
  nivel: require("./src/components/mapa/stationIcon/NivelLiquidoBlack.svg"),
  nivel_variacao: require("./src/components/mapa/stationIcon/NivelLiquidoBlack.svg"),
  vazao: require("./src/components/mapa/stationIcon/NivelLiquidoBlack.svg"),
  chuva: require("./src/components/mapa/stationIcon/ChuvaBlack.svg"),
  chuva_taxa_max: require("./src/components/mapa/stationIcon/ChuvaBlack.svg"),
  temperatura: require("./src/components/mapa/stationIcon/TemperaturaBlack.svg"),
  senstermica: require("./src/components/mapa/stationIcon/TemperaturaBlack.svg"),
  umidade: require("./src/components/mapa/stationIcon/UmidadeBlack.svg"),
  vento: require("./src/components/mapa/stationIcon/VentoBlack.svg"),
  vento_max: require("./src/components/mapa/stationIcon/RajadaVentoBlack.svg"),
  vento_direcao: require("./src/components/mapa/stationIcon/VentoBlack.svg"),
  pressao: require("./src/components/mapa/stationIcon/BarometroBlack.svg"),
  radiacao: require("./src/components/mapa/stationIcon/Piranometro.svg"),
};

// Fundo do card: mesma fórmula do aside de dispositivo (radial a 12% partindo do canto
// superior esquerdo). A COR vem da própria grandeza, definida uma única vez no mixin —
// é a mesma que pinta o pin no mapa e a linha do gráfico, então o card não pode divergir.
function gradienteDe(hex) {
  if (!hex) return null;

  const limpo = String(hex).replace("#", "");
  if (limpo.length !== 6) return null;

  const r = parseInt(limpo.slice(0, 2), 16);
  const g = parseInt(limpo.slice(2, 4), 16);
  const b = parseInt(limpo.slice(4, 6), 16);
  if ([r, g, b].some(isNaN)) return null;

  return (
    `radial-gradient(ellipse at 0% 0%, rgba(${r}, ${g}, ${b}, 0.12) 0%, ` +
    "rgba(255, 255, 255, 0.12) 100%)"
  );
}

// Ordem dos cards. Não é a ordem em que a origem devolve: nível e chuva vêm primeiro
// porque são o motivo de alguém abrir uma estação da Defesa Civil; o resto é contexto.
const ORDEM = [
  "nivel",
  "nivel_variacao",
  "vazao",
  "chuva",
  "chuva_taxa_max",
  "temperatura",
  "senstermica",
  "umidade",
  "vento",
  "vento_max",
  "vento_direcao",
  "pressao",
  "radiacao",
];

// Janelas móveis, da mais curta para a mais longa. Todas terminam AGORA e cada uma
// contém a anterior — 24 h nunca é menor que 1 h.
// `curto` existe só para compor a faixa das linhas agrupadas ("15 min – 24 h"): com o
// rótulo por extenso a faixa quebrava em duas linhas e desalinhava a coluna inteira.
// Chaves = DefesaCivilApiClient.Converter. A origem trocou 15 min por 5 min e tirou 72 h.
const JANELAS_CHUVA = [
  { chave: "5min", label: "5 min", curto: "5 min" },
  { chave: "1h", label: "1 hora", curto: "1 h" },
  { chave: "3h", label: "3 horas", curto: "3 h" },
  { chave: "6h", label: "6 horas", curto: "6 h" },
  { chave: "12h", label: "12 horas", curto: "12 h" },
  { chave: "24h", label: "24 horas", curto: "24 h" },
  { chave: "48h", label: "48 horas", curto: "48 h" },
  { chave: "168h", label: "7 dias", curto: "7 d" },
];

// Totais de mês fechado — outra ordem de grandeza, escala própria. A origem deixou de
// publicar os totais de mês: as chaves não vêm mais e o bloco some sozinho (chuvaMes
// filtra ausentes). Mantido para quando/se voltarem.
const JANELAS_MES = [
  { chave: "mesAtual", label: "Mês atual" },
  { chave: "mesAnterior", label: "Mês anterior" },
];

// Um valor não-zero nunca pode virar barra invisível: abaixo disso o leitor lê "nada"
// onde havia chuva. 3% é o menor traço que ainda se enxerga na largura do painel.
const LARGURA_MINIMA = 3;

// Setas idênticas às do pin (getDefesaCivilTendenciaSvg no Dashboard) — o mesmo dado não
// pode ter dois desenhos diferentes conforme a tela.
const TENDENCIA_SVG = {
  subindo: '<path d="M7 17L17 7"/><path d="M9 7h8v8"/>',
  descendo: '<path d="M7 7l10 10"/><path d="M17 9v8H9"/>',
  estavel: '<path d="M6 12h12"/>',
};

// Subindo é o sinal de risco numa cheia (vermelho); descendo é alívio (verde); estável,
// neutro (azul). Mesmas cores do pin de terceiros, para as camadas não divergirem.
const TENDENCIA_META = {
  subindo: { label: "Subindo", cor: "#e63946" },
  descendo: { label: "Descendo", cor: "#5c9c3e" },
  estavel: { label: "Estável", cor: "#1e90ff" },
};

export default {
  name: "NewAsideDefesaCivil",
  components: { NewAsideMetricCard, NewAsideHistoryChart, NewAsideDataTable, Load },
  data() {
    return {
      ponto: null,
      detalhe: null,
      historico: null,
      dias: 5,
      expandedDayIndex: null,
      selectedGrandeza: null,
      historyCollapsed: false,
      loading: false,
      erro: null,
    };
  },
  computed: {
    localizacao() {
      if (!this.detalhe) return "";
      const partes = [];
      if (this.detalhe.rio) partes.push("Rio " + this.detalhe.rio);
      if (this.detalhe.bacia) partes.push(this.detalhe.bacia);
      if (this.detalhe.regiao) partes.push(this.detalhe.regiao);
      return partes.join(" · ");
    },

    days() {
      return (this.historico && this.historico.dias) || [];
    },

    expandedDay() {
      if (this.expandedDayIndex === null) return null;
      return this.days[this.expandedDayIndex] || null;
    },

    /// O último dia da lista é o de hoje — é nele que "agora" faz sentido.
    isDiaAtual() {
      return this.days.length > 0 && this.expandedDayIndex === this.days.length - 1;
    },

    /// Leitura mais recente por grandeza, vinda de /DefesaCivil/{codigo}.
    valoresAtuais() {
      return (this.detalhe && this.detalhe.valores) || {};
    },

    nivelAtual() {
      const v = this.valoresAtuais.nivel;
      return v == null ? null : Number(v);
    },

    /// A origem manda uma TAXA de variação por hora, não um rótulo. Perto de zero é ruído
    /// de sensor, então há uma zona morta antes de chamar de "subindo"/"descendo".
    /// Mesmos limiares do pin no Dashboard.
    tendencia() {
      const taxa = this.valoresAtuais.nivel_tendencia;
      if (taxa == null) return null;

      const n = Number(taxa);
      if (isNaN(n)) return null;

      const tipo = n > 0.01 ? "subindo" : n < -0.01 ? "descendo" : "estavel";
      const meta = TENDENCIA_META[tipo];
      return {
        tipo,
        label: meta.label,
        cor: meta.cor,
        icone:
          '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" ' +
          'stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
          TENDENCIA_SVG[tipo] +
          "</svg>",
      };
    },

    taxaTexto() {
      const taxa = this.valoresAtuais.nivel_tendencia;
      if (taxa == null) return "";
      const n = Number(taxa);
      if (isNaN(n)) return "";
      return `${n > 0 ? "+" : ""}${this.fmt(n, 3)} m/h`;
    },

    mainGrandeza() {
      if (this.historico && this.historico.grandezaPrincipal) return this.historico.grandezaPrincipal;
      const comDados = this.days.find((d) => Object.keys(d.resumo || {}).length);
      return comDados ? Object.keys(comDados.resumo)[0] : null;
    },

    mainUnit() {
      const meta = DEFESA_CIVIL_GRANDEZAS[this.mainGrandeza];
      return meta ? meta.unit : "";
    },

    mainEhSoma() {
      return this.ehSoma(this.mainGrandeza);
    },

    /// Cards em linhas de 2, como o grid dos outros asides.
    metricRows() {
      const dia = this.expandedDay;
      if (!dia) return [];

      const resumo = dia.resumo || {};
      const presentes = Object.keys(resumo);

      // ORDEM primeiro; grandeza nova que a origem passe a mandar cai no fim, sem sumir.
      const ordenadas = ORDEM.filter((g) => presentes.includes(g)).concat(
        presentes.filter((g) => !ORDEM.includes(g))
      );

      const cards = ordenadas.map((grandeza) => {
        const meta = DEFESA_CIVIL_GRANDEZAS[grandeza] || {
          label: this.humanize(grandeza),
          unit: "",
          color: null,
          casas: 1,
        };
        return {
          grandeza,
          label: meta.label,
          unit: meta.unit,
          iconSrc: ICONES[grandeza] || null,
          gradient: gradienteDe(meta.color),
          value: this.fmt(this.valorDoCard(grandeza, resumo[grandeza]), meta.casas),
        };
      });

      const rows = [];
      for (let i = 0; i < cards.length; i += 2) rows.push(cards.slice(i, i + 2));
      return rows;
    },

    selectedMeta() {
      return DEFESA_CIVIL_GRANDEZAS[this.selectedGrandeza] || null;
    },

    selectedLabel() {
      return this.selectedMeta
        ? this.selectedMeta.label.replace("\n", " ")
        : this.humanize(this.selectedGrandeza);
    },

    selectedCor() {
      return this.selectedMeta ? this.selectedMeta.color : "#006035";
    },

    selectedEhSoma() {
      return this.ehSoma(this.selectedGrandeza);
    },

    /// Horas do dia expandido para a grandeza selecionada, no formato que o gráfico e a
    /// tabela esperam ({dateTime, hour, avg}). Sem min/max: a origem só dá um valor.
    chartHours() {
      const dia = this.expandedDay;
      if (!dia || !this.selectedGrandeza) return [];

      return (dia.horas || [])
        .filter((h) => h.valores && h.valores[this.selectedGrandeza] != null)
        .map((h) => ({
          dateTime: h.dataHora,
          // 'hour' é o nome que NewAsideDataTable procura para saber quais horas do dia
          // faltam e marcá-las como sem leitura.
          hour: h.hora,
          avg: h.valores[this.selectedGrandeza],
        }));
    },

    tableColumns() {
      const casas = this.selectedMeta ? this.selectedMeta.casas : 1;
      return [
        { key: "hora", label: "hora", format: "time", width: "34%" },
        {
          key: "avg",
          label: this.selectedMeta ? this.selectedMeta.unit || "valor" : "valor",
          formatter: (row) => this.fmt(row.avg, casas),
        },
      ];
    },

    // A origem entrega o histórico agregado em BLOCOS DE 1 HORA (a query GraphQL pede
    // `interval: HOUR_1`), então `dataHora` é o instante do bucket — sempre hora cheia, não
    // a hora em que a estação mediu. `ultimaLeituraEm` é o timestamp real da última leitura
    // (o mesmo que decide se a estação está offline), então no dia corrente ele é a resposta
    // certa. Em dias já encerrados não existe equivalente, e aí o bucket é o que a fonte dá.
    ultimaHoraDoDia() {
      const dia = this.expandedDay;
      if (!dia || !dia.horas || !dia.horas.length) return null;

      if (this.horarioExato) return this.detalhe.ultimaLeituraEm;

      return dia.horas[dia.horas.length - 1].dataHora;
    },

    // Dias encerrados só têm precisão de hora (ver ultimaHoraDoDia), então o rótulo diz
    // "bloco" em vez de fingir que aquele minuto veio da estação.
    horarioExato() {
      return Boolean(this.isDiaAtual && this.detalhe && this.detalhe.ultimaLeituraEm);
    },

    /// Casa os cantos do painel com a posição da aba de dia ativa.
    expandedContentRadius() {
      const isFirst = this.expandedDayIndex === 0;
      const isLast = this.expandedDayIndex === this.days.length - 1;
      return { borderRadius: `${isFirst ? "0" : "10px"} ${isLast ? "0" : "10px"} 10px 10px` };
    },

    /// Acumulados brutos vindos do backend.
    chuvaAcumulada() {
      return (this.detalhe && this.detalhe.chuvaAcumulada) || {};
    },

    /// Estação sem pluviômetro devolve tudo nulo/zero — aí o bloco inteiro some, em vez
    /// de mostrar onze linhas de "0,0 mm".
    temChuva() {
      const a = this.chuvaAcumulada;
      return JANELAS_CHUVA.concat(JANELAS_MES).some((j) => Number(a[j.chave]) > 0);
    },

    chuva24h() {
      return this.fmt(this.chuvaAcumulada["24h"], 1);
    },

    /// Uma frase sobre o estado, lida antes de qualquer barra. Usa a janela mais curta
    /// (5 min) para dizer se está chovendo AGORA — o acumulado de 24 h não distingue
    /// "choveu de madrugada" de "está chovendo neste momento".
    chuvaEstado() {
      const a = this.chuvaAcumulada;
      const agora = Number(a["5min"]) || 0;
      const h24 = Number(a["24h"]) || 0;

      if (agora > 0) return "Chovendo agora";
      if (h24 > 0) return "Choveu nas últimas 24 h, parado agora";

      const h168 = Number(a["168h"]) || 0;
      if (h168 > 0) return "Sem chuva nas últimas 24 h";
      return "Sem chuva na última semana";
    },

    /// Janelas móveis, escaladas entre si (o maior é sempre o de 7 dias, por definição).
    ///
    /// Sequências de zeros consecutivos viram UMA linha ("15 min – 12 horas"): seis linhas
    /// vazias empurram para fora da tela justamente as janelas que têm chuva, e não
    /// acrescentam nada que uma linha só não diga.
    chuvaJanelas() {
      const a = this.chuvaAcumulada;

      const presentes = JANELAS_CHUVA.filter((j) => a[j.chave] != null).map((j) => ({
        chave: j.chave,
        label: j.label,
        curto: j.curto,
        valor: Number(a[j.chave]) || 0,
      }));

      if (!presentes.length) return [];

      const maior = Math.max(...presentes.map((j) => j.valor));
      const linhas = [];
      let i = 0;

      while (i < presentes.length) {
        if (presentes[i].valor > 0) {
          const j = presentes[i];
          const bruto = maior > 0 ? (j.valor / maior) * 100 : 0;
          linhas.push({
            chave: j.chave,
            label: j.label,
            zero: false,
            percentual: Math.max(LARGURA_MINIMA, Math.round(bruto)),
            texto: this.fmt(j.valor, 1),
          });
          i++;
          continue;
        }

        // Corrida de zeros: agrupa da primeira à última.
        const inicio = i;
        while (i < presentes.length && presentes[i].valor === 0) i++;
        const fim = i - 1;

        linhas.push({
          chave: "zeros-" + presentes[inicio].chave,
          label:
            inicio === fim
              ? presentes[inicio].label
              : `${presentes[inicio].curto} – ${presentes[fim].curto}`,
          zero: true,
          percentual: 0,
          texto: this.fmt(0, 1),
        });
      }

      return linhas;
    },

    /// Meses, com escala própria — é o que impede o mês anterior de achatar as janelas
    /// curtas, que era o defeito do card antigo.
    chuvaMes() {
      const a = this.chuvaAcumulada;

      const presentes = JANELAS_MES.filter((j) => a[j.chave] != null).map((j) => ({
        chave: j.chave,
        label: j.label,
        valor: Number(a[j.chave]) || 0,
      }));

      if (!presentes.length || presentes.every((j) => !j.valor)) return [];

      const maior = Math.max(...presentes.map((j) => j.valor));

      return presentes.map((j) => {
        const bruto = maior > 0 ? (j.valor / maior) * 100 : 0;
        return {
          chave: j.chave,
          label: j.label,
          percentual: j.valor > 0 ? Math.max(LARGURA_MINIMA, Math.round(bruto)) : 0,
          texto: this.fmt(j.valor, 1),
        };
      });
    },

    /// O mês corrente está em andamento, então comparar com o anterior só é honesto como
    /// proporção — e dito como proporção, não como "caiu 73%".
    chuvaMesComparacao() {
      const atual = Number(this.chuvaAcumulada.mesAtual);
      const anterior = Number(this.chuvaAcumulada.mesAnterior);

      if (!anterior || isNaN(atual) || isNaN(anterior)) return "";

      const pct = Math.round((atual / anterior) * 100);
      return `Mês atual em ${pct}% do total do mês anterior (mês ainda em curso)`;
    },
  },
  methods: {
    // Chamado pelo Dashboard ao clicar no pin.
    load(ponto) {
      this.ponto = ponto;
      this.detalhe = null;
      this.historico = null;
      this.expandedDayIndex = null;
      this.selectedGrandeza = null;
      this.erro = null;
      this.carregar();
    },

    recarregar() {
      if (this.ponto) this.load(this.ponto);
    },

    async carregar() {
      if (!this.ponto || !this.ponto.codigo) return;

      this.loading = true;
      try {
        const codigo = encodeURIComponent(this.ponto.codigo);
        const [resDetalhe, resHistorico] = await Promise.all([
          this.urlGet(`/DefesaCivil/${codigo}`),
          // A série é o extra: se ela falhar, o painel ainda mostra a leitura atual.
          this.urlGet(`/DefesaCivil/${codigo}/Hourly?dias=${this.dias}`).catch((e) => {
            console.warn("Erro ao carregar série horária da Defesa Civil:", e);
            return null;
          }),
        ]);

        this.detalhe = resDetalhe.data;
        this.historico = resHistorico ? resHistorico.data : null;

        // Abre no dia mais recente, mas com NENHUM card selecionado: o gráfico é um
        // detalhamento que o usuário pede clicando, não algo que deva abrir por cima da
        // leitura atual. `selectedGrandeza` segue null até o primeiro clique.
        if (this.days.length) {
          this.expandedDayIndex = this.days.length - 1;
        }
      } catch (e) {
        console.error("Erro ao carregar estação da Defesa Civil:", e);
        this.erro = "Não foi possível carregar os dados desta estação.";
      } finally {
        this.loading = false;
      }
    },

    temDados(dia) {
      return !!(dia && dia.resumo && Object.keys(dia.resumo).length);
    },

    ehSoma(grandeza) {
      const meta = DEFESA_CIVIL_GRANDEZAS[grandeza];
      return !!(meta && meta.soma);
    },

    onDaySelect(index) {
      this.expandedDayIndex = index;

      // Sem card aberto, trocar de dia não abre nenhum: só ajusta quando JÁ havia um
      // gráfico na tela e aquela grandeza não existe no dia novo (estações têm buracos).
      if (!this.selectedGrandeza) return;

      const dia = this.days[index];
      if (dia && (!dia.resumo || !dia.resumo[this.selectedGrandeza])) {
        const chaves = Object.keys((dia && dia.resumo) || {});
        this.selectedGrandeza = chaves.length ? chaves[0] : null;
      }
    },

    // Toggle: clicar no card já selecionado fecha o gráfico.
    selectCard(grandeza) {
      this.selectedGrandeza = this.selectedGrandeza === grandeza ? null : grandeza;
    },

    isRowSelected(row) {
      return row.some((c) => c.grandeza === this.selectedGrandeza);
    },

    // Valor do card: no dia de hoje mostra a LEITURA ATUAL (como no aside de dispositivo),
    // não a média do dia — a média achata e chega a divergir vários graus do que a estação
    // está medindo agora. Chuva foge à regra: o que interessa é o total do dia, e o
    // "atual" de chuva no /Pins é uma janela móvel de 1h, não o dia.
    valorDoCard(grandeza, stat) {
      if (this.ehSoma(grandeza)) return stat ? stat.soma : null;

      if (this.isDiaAtual) {
        const atual = this.valoresAtuais[grandeza];
        if (atual != null) return atual;
      }

      return stat ? stat.media : null;
    },

    dayLabel(data) {
      const d = new Date(data + "T00:00:00");
      if (isNaN(d.getTime())) return data;
      return `${WEEK_ABBR[d.getDay()]}. ${d.getDate()}`;
    },

    dayMainValue(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      if (!stat) return "--";
      const casas = this.selectedCasas(this.mainGrandeza);
      return this.fmt(this.mainEhSoma ? stat.soma : stat.media, casas);
    },

    dayMainRange(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      if (!stat || this.mainEhSoma) return "";
      const casas = this.selectedCasas(this.mainGrandeza);
      return `${this.fmt(stat.minima, casas)}/${this.fmt(stat.maxima, casas)}`;
    },

    // Usados nas abas de dias já encerrados, onde só mín/máx interessam.
    dayMainMax(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      return stat ? this.fmt(stat.maxima, this.selectedCasas(this.mainGrandeza)) : "--";
    },

    dayMainMin(dia) {
      const stat = dia.resumo && dia.resumo[this.mainGrandeza];
      return stat ? this.fmt(stat.minima, this.selectedCasas(this.mainGrandeza)) : "--";
    },

    selectedCasas(grandeza) {
      const meta = DEFESA_CIVIL_GRANDEZAS[grandeza];
      return meta ? meta.casas : 1;
    },

    fmt(v, casas = 1) {
      if (v == null || v === "") return "--";
      const n = Number(v);
      if (isNaN(n)) return String(v);
      return n.toLocaleString("pt-BR", {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      });
    },

    formatDate(iso) {
      if (!iso) return "--";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? "--" : d.toLocaleDateString("pt-BR");
    },

    formatTime(iso) {
      if (!iso) return "--";
      const d = new Date(iso);
      return isNaN(d.getTime())
        ? "--"
        : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },

    humanize(chave) {
      if (!chave) return "";
      const t = String(chave).replace(/_/g, " ").trim();
      return t.charAt(0).toUpperCase() + t.slice(1);
    },
  },
};
</script>

<style scoped>
/* Tokens e classes espelham NewAsideExternalDevice.vue para os asides ficarem idênticos. */
.na-label { font-size: 13px; font-weight: 600; color: #333; }

.na-dc-error { text-align: center; padding: 40px 16px; color: #6c757d; }
.na-dc-retry {
  margin-top: 12px; border: 1px solid #006035; background: #fff; color: #006035;
  border-radius: 6px; padding: 6px 14px; font-size: 13px; cursor: pointer;
}
.na-dc-empty { text-align: center; color: #6c757d; font-size: 13px; padding: 20px 0; margin: 0; }

.na-dc-source { margin-bottom: 10px; }
.na-dc-source-box {
  background: #F4F7F2; border: 1px solid #DDE5D8; border-radius: 10px; padding: 10px 12px;
}
.na-dc-source-main { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.na-dc-source-name { font-size: 14px; font-weight: 700; color: #2f3b2a; }
.na-dc-source-code { font-size: 12px; color: #6c757d; font-variant-numeric: tabular-nums; }
.na-dc-source-sub { display: block; margin-top: 4px; font-size: 12px; color: #6c757d; }

.na-section { margin-bottom: 10px; }
.na-dc-leitura {
  background: #fff; border: 1px solid #E4E4E4; border-radius: 10px; padding: 8px 12px;
}
/* Estação muda: o bloco inteiro fica âmbar para o número não ser lido como atual. */
.na-dc-leitura.is-offline { background: #FFF6E5; border-color: #F0D9A8; }
.na-dc-leitura-row, .na-leitura-row { display: flex; align-items: center; gap: 8px; }
.na-dc-leitura-label, .na-leitura-label { font-size: 12px; color: #6c757d; flex: 1; }
.na-dc-leitura-date, .na-dc-leitura-time,
.na-leitura-date, .na-leitura-time {
  font-size: 12px; font-weight: 600; color: #333; font-variant-numeric: tabular-nums;
}
.na-leitura { background: #fff; border-radius: 8px; padding: 8px 10px; margin-bottom: 8px; }

.na-historico-header { margin: 14px 0 8px; }
.na-historico-title { font-size: 13px; font-weight: 600; color: #333; }

/* ===== Abas de dia =====
   Tokens copiados de NewAsideDayItem.vue / NewAsideDevice.vue para o painel da Defesa
   Civil ficar visualmente idêntico ao do dispositivo. Não dá para reusar aquele
   componente: ele é acoplado ao shape do device (resume.rainfallTotal, stationType…),
   e adaptar os dados daqui àquele formato exigiria fingir campos que não existem. */
.na-days-container { position: relative; }
.na-days-row {
  display: flex;
  overflow-x: auto;
  gap: 3px;
  padding: 0;
  position: relative;
  z-index: 2;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.na-days-row::-webkit-scrollbar { display: none; }

.na-day-item {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  border-radius: 8px;
  background: #F2F4F1;
  text-align: center;
  padding: 6px 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  /* Altura fixa: o dia corrente tem 3 linhas (valor, unidade, faixa) e os encerrados
     também (máx, mín, unidade), mas as alturas de fonte diferem. Sem isto a linha de
     abas fica com dentes. */
  justify-content: flex-start;
  min-height: 62px;
}
.na-day-item:hover { background: #e8ebe6; }
/* A aba ativa se funde com o painel de baixo (borda inferior transparente + -1px). */
.na-day-item.active {
  background-color: #FFFFFF;
  border: 1px solid #C3D9AD;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
  position: relative;
  z-index: 3;
  margin-bottom: -1px;
}
/* Dia sem nenhuma leitura: mesma aba, esmaecida — sumir daria a entender que o dia
   não existe, em vez de que faltou dado. */
.na-day-item.is-empty { opacity: 0.55; }

.na-day-text { font-size: 11px; font-weight: 600; color: #333; }
.na-day-rain { display: flex; flex-direction: column; align-items: center; line-height: 1; }
.na-day-value { font-size: 16px; font-weight: 700; color: #006035; }
.na-day-unit { font-size: 10px; color: #666; }
.na-day-temp-range { font-size: 10px; color: #666; }

/* Abas de dias encerrados: máx/mín empilhados, mesmo visual dos outros asides do mapa.
   Altura casada com a do dia corrente (valor + unidade + faixa) para a linha de abas
   não ficar com dentes. */
.na-day-minmax {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  line-height: 1.1;
  padding: 1px 0 0;
}
.na-day-minmax-item { display: flex; align-items: baseline; gap: 3px; }
.na-day-minmax-cap {
  font-size: 8px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: .2px;
}
.na-day-minmax-val {
  font-size: 12px;
  font-weight: 700;
  color: #006035;
  font-variant-numeric: tabular-nums;
}
.na-day-minmax-unit { font-size: 10px; color: #666; }

.na-expanded-content {
  border: 1px solid #C3D9AD;
  border-radius: 10px;
  background: #FFFFFF80;
  padding: 10px;
  position: relative;
  z-index: 1;
  margin-top: 0;
  animation: na-expand 0.3s ease-out;
}
@keyframes na-expand {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== Situação do rio (só no dia corrente) ===== */
.na-dc-rio {
  background: #fff; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px;
}
.na-dc-rio-main { display: flex; align-items: baseline; justify-content: space-between; }
.na-dc-rio-label { font-size: 12px; color: #6c757d; }
.na-dc-rio-value { font-size: 16px; font-weight: 700; color: #2f3b2a; font-variant-numeric: tabular-nums; }
.na-dc-rio-trend { display: flex; align-items: center; gap: 6px; margin-top: 4px; }
.na-dc-trend-glyph { display: inline-flex; align-items: center; }
.na-dc-rio-trend-label { font-size: 12px; font-weight: 600; color: #333; }
.na-dc-rio-rate { font-size: 11px; color: #6c757d; font-variant-numeric: tabular-nums; }

.na-metrics-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }

/* Painel de gráfico — mesmo azul do card selecionado, formando um bloco contínuo. */
.na-chart-table-card {
  background: #EFF3FF; border: 1px solid #BBD1FF; border-radius: 10px;
  padding: 10px; margin-bottom: 8px;
}

/* ===== Card de chuva acumulada =====
   Duas escalas separadas de propósito (janelas móveis × mês). Numa escala só, o total
   do mês anterior é ~180x maior que uma janela de 48 h e reduz todas as barras curtas a
   menos de um pixel — foi o que motivou o redesenho. */
.na-rain-card {
  background: #FFFFFF;
  border: 1px solid #E4E4E4;
  border-radius: 10px;
  padding: 14px;
}

/* Figuras proporcionais (sem tabular-nums): num número grande e isolado, a largura fixa
   de dígito abre buracos entre os algarismos. */
.na-rain-hero { display: flex; align-items: baseline; gap: 4px; }
.na-rain-hero-value { font-size: 30px; font-weight: 600; line-height: 1; color: #2f3b2a; }
.na-rain-hero-unit { font-size: 14px; font-weight: 600; color: #6c757d; }
.na-rain-hero-label { margin-top: 4px; font-size: 12px; color: #6c757d; }
.na-rain-state {
  display: inline-block; margin-top: 8px; padding: 3px 8px; border-radius: 999px;
  background: #EFF3FF; color: #2563EB; font-size: 11px; font-weight: 600;
}

.na-rain-group { margin-top: 14px; }
.na-rain-group-title {
  font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: #9aa0a6; margin-bottom: 6px;
}

.na-rain-row { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
.na-rain-label {
  font-size: 12px; color: #6c757d; width: 100px; flex: 0 0 auto;
  /* Nunca quebrar: uma linha de rótulo em duas altera a altura da linha e desalinha
     toda a coluna de barras. */
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Trilho num tom claro do MESMO azul da barra: o estado se lê ao longo de todo o
   comprimento, em vez de a barra flutuar sobre um cinza sem relação com ela. */
.na-rain-track {
  flex: 1; height: 8px; background: #DBEAFE; border-radius: 4px; overflow: hidden;
}
/* Ponta arredondada no fim do dado, reta na base — a barra cresce de uma linha só. */
.na-rain-fill {
  display: block; height: 100%; background: #3B82F6;
  border-radius: 0 4px 4px 0; transition: width 0.3s ease;
}
/* Valores alinhados em coluna: aqui tabular-nums é o certo. */
.na-rain-value {
  font-size: 12px; font-weight: 600; color: #333; width: 52px; text-align: right;
  flex: 0 0 auto; font-variant-numeric: tabular-nums;
}

/* Janela sem chuva: fica na lista (o leitor precisa saber que foi verificada), mas
   recua — sem trilho preenchido e com a tinta mais fraca. */
.na-rain-row.is-zero .na-rain-label,
.na-rain-row.is-zero .na-rain-value { color: #b0b5ba; font-weight: 500; }
.na-rain-row.is-zero .na-rain-track { background: #F1F3F5; }

.na-rain-note { margin-top: 8px; font-size: 11px; line-height: 1.4; color: #9aa0a6; }

.na-dc-fonte-nota {
  margin: 14px 0 4px; font-size: 11px; line-height: 1.4; color: #8a8a8a; text-align: center;
}
</style>
